<template>
  <div :class="rootClass">
    <div v-if="iconed" class="named-carts-field__icon">
      <slot name="icon" />
    </div>
    <textarea
      v-if="isTextarea"
      ref="field"
      :placeholder="label"
      :aria-label="label"
      :value="value"
      :disabled="disabled"
      :maxlength="maxlength"
      :readonly="readonly"
      class="named-carts-field__input named-carts-field__input--textarea"
      @click="$emit('click', $event)"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    />
    <Datepicker
      v-else-if="isDatepicker"
      :enable-time-picker="false"
      :aria-label="label"
      :min-date="min"
      :model-value="value"
      auto-apply
      model-type="yyyy-MM-dd"
      @update:model-value="$emit('update', $event)"
    >
      <template #dp-input="{ value: inputValue }">
        <input
          ref="field"
          :disabled="disabled"
          :aria-label="label"
          :placeholder="label"
          :value="inputValue"
          class="named-carts-field__input named-carts-field__input--date"
          type="text"
          @click="$emit('click', $event)"
          @blur="$emit('blur', $event)"
          @focus="$emit('focus', $event)"
        />
        <UseSvg v-if="!inputValue" icon="calendar" class="named-carts-field__icon-calendar" />
      </template>
    </Datepicker>
    <input
      v-else
      ref="field"
      :placeholder="label"
      :aria-label="label"
      :value="value"
      :disabled="disabled"
      :maxlength="maxlength"
      :min="min"
      :type="fieldType"
      class="named-carts-field__input"
      @click="$emit('click', $event)"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    />
    <div
      class="named-carts-field__label"
      :class="{ 'named-carts-field__label--static': isDatepicker }"
    >
      <div
        class="named-carts-field__label-text"
        :class="{ 'named-carts-field__marked-required': markRequired }"
      >
        {{ label }}

        <NamedCartsTooltip v-if="tooltipMsg" beta :tooltip-msg="tooltipMsg" />
      </div>
      <div
        v-if="placeholder"
        class="named-carts-field__label-placeholder"
        :class="{ 'named-carts-field__marked-required': markRequired }"
      >
        {{ placeholder }}
      </div>
      <UseSvg
        v-if="updated && value && valid"
        class="named-carts-field__updated icon icon--svg icon--check-circle-bold"
        icon="check-circle-bold"
      />
    </div>
    <div v-if="hint" class="named-carts-field__hint">{{ hint }}</div>
  </div>
</template>

<script>
import NamedCartsTooltip from "./NamedCartsTooltip.vue";
import UseSvg from "../UseSvg.vue";
import "@vuepic/vue-datepicker/dist/main.css";

const Datepicker = window.vuejsDatepicker;

export default {
  name: "NamedCartsField",
  components: { Datepicker, NamedCartsTooltip, UseSvg },
  props: {
    label: {
      type: String,
      required: true
    },
    isModalWindow: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    hint: {
      type: String,
      default: null
    },
    value: {
      type: String,
      default: ""
    },
    fieldType: {
      type: String,
      default: "text"
    },
    beta: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    valid: {
      type: Boolean,
      default: true
    },
    maxlength: {
      type: String,
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    min: {
      default: undefined,
      type: [Date, Number, String]
    },
    tooltipMsg: {
      type: String,
      default: undefined
    },
    animated: {
      type: Boolean,
      default: true
    },
    updated: {
      type: Boolean,
      default: false
    },
    isDatepicker: {
      type: Boolean,
      default: false
    },
    isTextarea: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: ""
    },
    markRequired: {
      type: Boolean,
      default: false
    }
  },
  emits: ["blur", "click", "focus", "update"],
  computed: {
    iconed() {
      return Boolean(this.$slots.icon);
    },
    rootClass() {
      return {
        "named-carts-field": true,
        "named-carts-field--invalid": !this.valid,
        "named-carts-field--animated": this.animated,
        "named-carts-field--iconed": this.iconed,
        "named-carts-field--modal-window": this.isModalWindow
      };
    }
  },
  methods: {
    focus() {
      this.$refs.field.focus();
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/settings";
@import "frontend/init/jiffy/input.scss";

.named-carts-field {
  $this: &;

  position: relative;

  &--invalid {
    #{$this}__label,
    #{$this}__hint {
      color: $red;
    }

    #{$this}__input {
      &,
      &:focus {
        border-color: $red;
      }

      &:focus-within + #{$this}__label,
      &:not(:placeholder-shown) + #{$this}__label {
        color: $red;
      }
    }
  }

  &--animated {
    #{$this}__label {
      transition-duration: 300ms;
    }
  }

  &--iconed {
    #{$this}__input {
      padding-left: 32px;
    }

    #{$this}__label {
      left: 32px;
    }
  }

  &--modal-window {
    #{$this}__input {
      display: block;
      border-color: $gray-border;
      padding-top: 10px;
      padding-bottom: 10px;
      height: 56px;
      line-height: normal;
    }

    #{$this}__label {
      font-size: 14px;
      line-height: 56px;
    }
  }

  &__label {
    position: absolute;
    display: flex;
    align-items: center;
    top: 0;
    left: 13px;
    font-weight: 400;
    font-size: 16px;
    line-height: 52px;
    color: $warm-grey;
    pointer-events: none;

    &--static {
      left: 13px;
      padding: 0 4px;
      background-color: $white;
      transform: translateY(-7px) translateX(-4px);
      color: $primary;
      font-size: 12px;
      line-height: 16px;
      pointer-events: auto;

      #{$this}__label-text {
        display: block;
      }

      #{$this}__label-placeholder {
        display: none;
      }
    }
  }

  &__hint {
    padding: 5px 12px;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: $warm-grey;
    margin-bottom: -10px;
  }

  &__input {
    position: relative;
    width: 100%;
    height: 52px;
    background: $white;
    border: 1px solid $gray-label;
    border-radius: 4px;
    appearance: none;
    padding: 8px 12px;
    font-weight: 400;
    font-size: 16px;
    line-height: 140%;
    color: $text-black;
    outline: none;

    &::placeholder {
      opacity: 0;
    }

    &:focus {
      border-color: $primary;
    }

    &:focus-within + #{$this}__label,
    &:not(:placeholder-shown) + #{$this}__label {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: $primary;
      left: 13px;
      transform: translateY(-7px) translateX(-4px);
      background: $white;
      padding: 0 4px;
      pointer-events: auto;

      #{$this}__label-text {
        display: block;
      }

      #{$this}__label-placeholder {
        display: none;
      }
    }

    &--textarea {
      resize: vertical;
    }
  }

  &__label-text {
    display: none;
  }

  &__updated {
    height: 14px;
    width: 14px;
    margin-left: 4px;
    margin-top: -1px;
    color: $green-success;
  }

  &__icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 12px;
  }

  &__icon-calendar {
    position: absolute;
    top: 14px;
    right: 16px;
    width: 24px;
    height: 24px;
    color: $text-black;
  }

  &__marked-required {
    @include label-visible(true);

    pointer-events: none;
  }
}
</style>
